import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { ModelData, TargetsModel, TargetSet, TargetPoint, TargetsMetadata, TargetValidationError } from '../models/interfaces';

interface WorksheetRow {
  rowNumber: number;
  cells: (string | undefined)[];
}

interface StationColumns {
  md: number;
  inclination: number;
  azimuth: number;
  tvd: number;
  northing: number;
  easting: number;
  comment: number;
  headerRowIndex: number;
  depthInFeet: boolean;
  anglesInDegrees: boolean;
  hasUnitsRow: boolean;
}

interface ExtractedMetadata {
  wellName: string;
  wellboreId: string;
  wellboreName: string;
  trajectoryName: string;
  fieldName: string;
  coordinateReferenceSystem: string;
  crsWarning?: string;
  azimuthReference: string;
  latitude: number;
  longitude: number;
  northing: number;
  easting: number;
  groundLevel: number;
  rkb: number;
  vsecOriginNs: number;
  vsecOriginEw: number;
  vsecAzimuth: number;
  uwi: string;
}

const EOCD_SIGNATURE = 0x06054b50;
const CENTRAL_DIRECTORY_SIGNATURE = 0x02014b50;
const LOCAL_FILE_HEADER_SIGNATURE = 0x04034b50;

/** Consumption Hub Template POINT NAME codes → supported location-type names (AC4). */
const POINT_NAME_MAP: Record<string, string> = {
  SHL: 'Surface Hole Location',
  PP: 'Kickoff / Proposed Point',
  FTP: 'First Target Point',
  LTP: 'Last Target Point',
  BHL: 'Bottom Hole Location'
};

/** Well-data columns required to recognize a file as the Consumption Hub Template (AC1). */
const TEMPLATE_WELL_COLUMNS = ['PAD', 'WELL NAME', 'POINT NAME', 'NORTHING', 'EASTING', 'TVD RKB'];

const normalizeInputLabel = (value: string | undefined): string =>
  (value ?? '').toString().normalize('NFKC').trim()
    .replace(/\s*\([^)]*\)\s*/g, ' ')
    .replace(/[^A-Za-z0-9]+/g, ' ')
    .trim();

const inputLabelKey = (value: string | undefined): string =>
  normalizeInputLabel(value).toUpperCase().replace(/\s+/g, '');

class ZipEntry {
  constructor(
    private buffer: ArrayBuffer,
    private meta: { compressionMethod: number; compressedSize: number; localHeaderOffset: number }
  ) {}

  get compressionMethod(): number {
    return this.meta.compressionMethod;
  }

  async arrayBuffer(): Promise<ArrayBuffer> {
    const { localHeaderOffset, compressedSize } = this.meta;
    const view = new DataView(this.buffer);

    if (view.getUint32(localHeaderOffset, true) !== LOCAL_FILE_HEADER_SIGNATURE) {
      throw new Error('Invalid local file header signature in XLSX file.');
    }

    const fileNameLength = view.getUint16(localHeaderOffset + 26, true);
    const extraFieldLength = view.getUint16(localHeaderOffset + 28, true);
    const dataStart = localHeaderOffset + 30 + fileNameLength + extraFieldLength;
    const compressedData = new Uint8Array(this.buffer, dataStart, compressedSize);

    if (this.compressionMethod === 0) {
      return compressedData.slice().buffer;
    }

    if (this.compressionMethod === 8) {
      if (typeof DecompressionStream === 'undefined') {
        throw new Error('This browser does not support XLSX decompression (DecompressionStream missing).');
      }

      const decompressedStream = new Blob([compressedData])
        .stream()
        .pipeThrough(new DecompressionStream('deflate-raw'));

      return await new Response(decompressedStream).arrayBuffer();
    }

    throw new Error(`Unsupported compression method ${this.compressionMethod} in XLSX file.`);
  }

  async text(): Promise<string> {
    const buffer = await this.arrayBuffer();
    return new TextDecoder('utf-8').decode(buffer);
  }
}

@Injectable({ providedIn: 'root' })
export class ExcelParserService {
  private crsMapping: Map<string, string> | null = null;
  private waMapping: Map<string, string> | null = null; // WellArchitect Projection System → platform CRS name
  private ambiguousCrsKeys: Set<string> = new Set(); // keys that map to more than one distinct CRS (resolution may depend on country/region)

  constructor(private http: HttpClient) {}

  /**
   * Parse an XLSX file (ArrayBuffer) into ModelData.
   */
  async parseModelWorkbook(arrayBuffer: ArrayBuffer): Promise<ModelData> {
    const rows = await this.readWorkbookRows(arrayBuffer);

    if (!this.crsMapping) {
      await this.loadCrsMapping();
    }

    return this.buildModelFromRows(rows);
  }

  /**
   * Parse a CSV text string into ModelData.
   */
  async parseModelCsv(csvText: string): Promise<ModelData> {
    if (!csvText || typeof csvText !== 'string') {
      throw new Error('Expected a non-empty CSV string.');
    }

    if (!this.crsMapping) {
      await this.loadCrsMapping();
    }

    const rows = this.parseCsvToRows(csvText);
    return this.buildModelFromRows(rows);
  }

  /**
   * Parse a Consumption Hub Template XLSX file (ArrayBuffer) into a structured
   * target model (one TargetSet per well, grouping its take-points).
   */
  async parseTargetsWorkbook(arrayBuffer: ArrayBuffer): Promise<TargetsModel> {
    const rows = await this.readWorkbookRows(arrayBuffer);
    await this.loadTargetsCrsMappingIfNeeded(rows);
    return this.buildTargetsModelFromRows(rows);
  }

  /**
   * Parse a Consumption Hub Template CSV string into a structured target model.
   */
  async parseTargetsCsv(csvText: string): Promise<TargetsModel> {
    if (!csvText || typeof csvText !== 'string') {
      throw new Error('Expected a non-empty CSV string.');
    }

    const rows = this.parseCsvToRows(csvText);
    await this.loadTargetsCrsMappingIfNeeded(rows);
    return this.buildTargetsModelFromRows(rows);
  }

  private async loadTargetsCrsMappingIfNeeded(rows: WorksheetRow[]): Promise<void> {
    const hasCrsMetadata =
      !!this.findLabeledValue(rows, [/geodetic\s*system/i]) &&
      !!this.findLabeledValue(rows, [/geodetic\s*datum/i]) &&
      !!this.findLabeledValue(rows, [/geodetic\s*zone/i, /map\s*zone/i]);
    if (hasCrsMetadata && !this.crsMapping) {
      await this.loadCrsMapping();
    }
  }

  /**
   * Read the primary worksheet of an XLSX workbook into rows. Shared by the
   * model and targets parsers.
   */
  private async readWorkbookRows(arrayBuffer: ArrayBuffer): Promise<WorksheetRow[]> {
    if (!(arrayBuffer instanceof ArrayBuffer)) {
      throw new Error('Expected an ArrayBuffer when reading the XLSX file.');
    }

    const view = new DataView(arrayBuffer);
    const entries = this.readCentralDirectory(arrayBuffer, view);

    const sheetEntryName = entries.has('xl/worksheets/sheet1.xml')
      ? 'xl/worksheets/sheet1.xml'
      : Array.from(entries.keys()).find((name) => name.startsWith('xl/worksheets/sheet'));

    if (!sheetEntryName) {
      throw new Error('The XLSX workbook does not contain a worksheet.');
    }

    const sheetEntry = entries.get(sheetEntryName);
    const sharedStringsEntry = entries.get('xl/sharedStrings.xml');

    if (!sheetEntry) {
      throw new Error('Unable to read the primary worksheet from the XLSX file.');
    }

    const [sheetXml, sharedStringsXml] = await Promise.all([
      sheetEntry.text(),
      sharedStringsEntry ? sharedStringsEntry.text() : Promise.resolve('')
    ]);

    const sharedStrings = this.parseSharedStrings(sharedStringsXml);
    return this.parseXlsxWorksheet(sheetXml, sharedStrings);
  }

  // --- Targets model building (Consumption Hub Template) ---

  /**
   * Build a structured target model from worksheet rows conforming to the
   * Consumption Hub Template standard.
   *
   * AC1 — recognizes the template by locating a header row containing PAD,
   * WELL NAME, POINT NAME, Northing (ft), Easting (ft) and TVD RKB; files
   * without all six columns are rejected outright (not a template file).
   * AC2 — extracts the North Reference / Coordinate Type / Units / RKB
   * metadata rows that precede the header.
   * AC3/AC5 — reads each well record and groups all points for the same
   * WELL NAME into a single TargetSet.
    * Templates with WELLBORE NAME are self-contained: parent ids are derived
    * from WELL NAME / WELLBORE NAME using the same convention as trajectories.
   * AC4 — maps POINT NAME codes (SHL/PP/FTP/LTP/BHL) to their full location
   * names.
   * AC6/AC7 — validates every row and collects actionable errors (row,
   * column, message, suggestion); invalid rows are excluded from the
   * returned sets rather than failing the whole file.
   */
  private buildTargetsModelFromRows(rows: WorksheetRow[]): TargetsModel {
    const headerRowIndex = rows.findIndex((row) => {
      const normalized = row.cells.map(inputLabelKey);
      return TEMPLATE_WELL_COLUMNS.every((col) => normalized.includes(inputLabelKey(col)));
    });

    if (headerRowIndex < 0) {
      throw new Error(
        'File does not match the Consumption Hub Template. Expected a header row with columns: ' +
        'PAD, WELL NAME, POINT NAME, Northing (ft), Easting (ft), TVD RKB.'
      );
    }

    const header = rows[headerRowIndex].cells.map(inputLabelKey);
    const columnOf = (name: string) => header.indexOf(inputLabelKey(name));
    const padCol = columnOf('PAD');
    const wellCol = columnOf('WELL NAME');
    const pointCol = columnOf('POINT NAME');
    const wellboreCol = columnOf('WELLBORE NAME');
    const isOffShoreCol = columnOf('IS OFF SHORE');
    const groundLevelCol = columnOf('GROUND LEVEL ELEVATION');
    const surfaceNorthingCol = columnOf('SURFACE NORTHING');
    const surfaceEastingCol = columnOf('SURFACE EASTING');
    const latitudeCol = columnOf('LATITUDE');
    const longitudeCol = columnOf('LONGITUDE');
    const northingCol = columnOf('NORTHING');
    const eastingCol = columnOf('EASTING');
    const tvdCol = columnOf('TVD RKB');

    const errors: TargetValidationError[] = [];
    const metadata = this.extractTargetsMetadata(rows, headerRowIndex, errors);
    const provisionsParents = wellboreCol >= 0;

    const toMetres = (v: number) => v * 0.3048;
    const trimmed = (v: string | undefined) => (v ?? '').toString().trim();
    const parseNumeric = (v: string | undefined): number | undefined => {
      const s = trimmed(v);
      if (s === '') return undefined;
      const n = Number(s);
      return Number.isFinite(n) ? n : NaN; // NaN signals "present but not numeric"
    };

    // Map preserves insertion order, so the first-seen well order is retained.
    const setsByWell = new Map<string, TargetSet>();

    for (let i = headerRowIndex + 1; i < rows.length; i++) {
      const row = rows[i];
      const cells = row.cells;
      const pad = trimmed(cells[padCol]);
      const wellName = trimmed(cells[wellCol]);
      const wellboreName = wellboreCol >= 0 ? trimmed(cells[wellboreCol]) : '';
      const isOffShoreRaw = isOffShoreCol >= 0 ? trimmed(cells[isOffShoreCol]) : '';
      const groundLevelRaw = groundLevelCol >= 0 ? trimmed(cells[groundLevelCol]) : '';
      const surfaceNorthingRaw = surfaceNorthingCol >= 0 ? trimmed(cells[surfaceNorthingCol]) : '';
      const surfaceEastingRaw = surfaceEastingCol >= 0 ? trimmed(cells[surfaceEastingCol]) : '';
      const latitudeRaw = latitudeCol >= 0 ? trimmed(cells[latitudeCol]) : '';
      const longitudeRaw = longitudeCol >= 0 ? trimmed(cells[longitudeCol]) : '';
      const pointRaw = trimmed(cells[pointCol]);
      const northingRaw = trimmed(cells[northingCol]);
      const eastingRaw = trimmed(cells[eastingCol]);
      const tvdRaw = trimmed(cells[tvdCol]);

      const isBlankRow = !pad && !wellName && !wellboreName && !isOffShoreRaw &&
        !groundLevelRaw && !surfaceNorthingRaw && !surfaceEastingRaw && !latitudeRaw && !longitudeRaw && !pointRaw &&
        !northingRaw && !eastingRaw && !tvdRaw;
      if (isBlankRow) continue; // blank/separator rows between wells

      let rowValid = true;

      // PAD is descriptive metadata only — it is never sent to the Consumption
      // Hub API (TargetSetDto/TargetDto have no PAD field), so a blank PAD
      // (e.g. from a merged cell spanning multiple rows in the source
      // workbook) does not block the row.

      if (!wellName) {
        errors.push({
          row: row.rowNumber,
          column: 'WELL NAME',
          message: 'WELL NAME is required.',
          suggestion: 'Enter the well name in the WELL NAME column.'
        });
        rowValid = false;
      }

      if (provisionsParents) {
        if (!wellboreName) {
          errors.push({
            row: row.rowNumber,
            column: 'WELLBORE NAME',
            message: 'WELLBORE NAME is required.',
            suggestion: 'Enter the wellbore name to send to the wellbores endpoint.'
          });
          rowValid = false;
        }
      }

      let isOffShore = false;
      if (isOffShoreRaw) {
        const normalized = isOffShoreRaw.toLowerCase();
        if (['true', 'yes', '1'].includes(normalized)) {
          isOffShore = true;
        } else if (!['false', 'no', '0'].includes(normalized)) {
          errors.push({
            row: row.rowNumber,
            column: 'IS OFF SHORE',
            message: 'IS OFF SHORE must be true or false.',
            suggestion: 'Enter true for an offshore well, false for a land well, or leave it blank to default to false.'
          });
          rowValid = false;
        }
      }

      let groundLevel: number | undefined;
      if (groundLevelCol >= 0) {
        groundLevel = parseNumeric(groundLevelRaw);
        if (groundLevel === undefined) {
          errors.push({
            row: row.rowNumber,
            column: 'Ground Level Elevation',
            message: 'Ground Level Elevation is required when the column is present.',
            suggestion: 'Enter the surveyed ground-level elevation for this well in the workbook Units.'
          });
          rowValid = false;
        } else if (Number.isNaN(groundLevel)) {
          errors.push({
            row: row.rowNumber,
            column: 'Ground Level Elevation',
            message: 'Value is not numeric.',
            suggestion: 'Enter a numeric ground-level elevation (e.g. 2859).'
          });
          rowValid = false;
        }
      }

      let pointCode = '';
      let pointFullName = '';
      if (!pointRaw) {
        errors.push({
          row: row.rowNumber,
          column: 'POINT NAME',
          message: 'POINT NAME is required.',
          suggestion: 'Enter one of the supported codes (SHL, PP, FTP, LTP, BHL) in the POINT NAME column.'
        });
        rowValid = false;
      } else {
        pointCode = pointRaw.toUpperCase();
        pointFullName = POINT_NAME_MAP[pointCode];
        if (!pointFullName) {
          errors.push({
            row: row.rowNumber,
            column: 'POINT NAME',
            message: `Unsupported POINT NAME value "${pointRaw}".`,
            suggestion: 'Use one of the supported codes: SHL, PP, FTP, LTP, BHL.'
          });
          rowValid = false;
        }
      }

      const northing = parseNumeric(northingRaw);
      if (northing === undefined) {
        errors.push({
          row: row.rowNumber,
          column: 'Northing (ft)',
          message: 'Northing (ft) is required.',
          suggestion: 'Enter a numeric northing value (e.g. 1444236.92) in the Northing (ft) column.'
        });
        rowValid = false;
      } else if (Number.isNaN(northing)) {
        errors.push({
          row: row.rowNumber,
          column: 'Northing (ft)',
          message: 'Value is not numeric.',
          suggestion: 'Enter a numeric value (e.g. 1444236.92) in the Northing (ft) column.'
        });
        rowValid = false;
      }

      const easting = parseNumeric(eastingRaw);
      if (easting === undefined) {
        errors.push({
          row: row.rowNumber,
          column: 'Easting (ft)',
          message: 'Easting (ft) is required.',
          suggestion: 'Enter a numeric easting value (e.g. 760333.73) in the Easting (ft) column.'
        });
        rowValid = false;
      } else if (Number.isNaN(easting)) {
        errors.push({
          row: row.rowNumber,
          column: 'Easting (ft)',
          message: 'Value is not numeric.',
          suggestion: 'Enter a numeric value (e.g. 760333.73) in the Easting (ft) column.'
        });
        rowValid = false;
      }

      // TVD RKB is optional (e.g. SHL/PP rows), but must be numeric when supplied.
      let tvd: number | undefined;
      if (tvdRaw !== '') {
        tvd = parseNumeric(tvdRaw);
        if (tvd !== undefined && Number.isNaN(tvd)) {
          errors.push({
            row: row.rowNumber,
            column: 'TVD RKB',
            message: 'Value is not numeric.',
            suggestion: 'Enter a numeric value (e.g. -6147.13) in the TVD RKB column, or leave it blank.'
          });
          rowValid = false;
        }
      }

      if (!rowValid) continue;

      const setKey = wellName;
      if (!setsByWell.has(setKey)) {
        const set: TargetSet = {
          id: `${wellName}-Targets`,
          name: `${wellName} Targets`,
          wellName,
          targets: []
        };
        if (pad) set.pad = pad;
        if (wellboreName) set.wellboreName = wellboreName;
        set.isOffShore = isOffShore;
        if (groundLevel !== undefined && !Number.isNaN(groundLevel)) {
          set.groundLevel = toMetres(groundLevel);
          if (metadata.rkb !== undefined) set.rkb = toMetres(groundLevel + metadata.rkb);
        }
        setsByWell.set(setKey, set);
      }

      const set = setsByWell.get(setKey)!;
      if (!set.location && metadata.coordinateReferenceSystem) {
        const azimuthReference = metadata.azimuthReference?.trim().toLowerCase();
        const coordinateType = metadata.coordinateType?.trim().toLowerCase();
        if (azimuthReference === 'grid' && coordinateType === 'grid' && surfaceNorthingRaw && surfaceEastingRaw) {
          const surfaceNorthing = parseNumeric(surfaceNorthingRaw);
          const surfaceEasting = parseNumeric(surfaceEastingRaw);
          if (surfaceNorthing !== undefined && surfaceEasting !== undefined &&
              !Number.isNaN(surfaceNorthing) && !Number.isNaN(surfaceEasting)) {
            set.location = {
              coordinate_reference_system: metadata.coordinateReferenceSystem,
              coordinates_type: 'Grid',
              azimuth_reference: 'Grid',
              northing: surfaceNorthing,
              easting: surfaceEasting
            };
          } else {
            errors.push({
              row: row.rowNumber,
              column: 'Surface Northing / Surface Easting',
              message: 'Surface coordinates must both be numeric.',
              suggestion: 'Enter numeric Surface Northing and Surface Easting values in the coordinate reference system\'s native units.'
            });
          }
        } else if (azimuthReference === 'true' && coordinateType === 'geodetic' && latitudeRaw && longitudeRaw) {
          const latitude = this.parseGeographicCoordinate(latitudeRaw, 'latitude');
          const longitude = this.parseGeographicCoordinate(longitudeRaw, 'longitude');
          if (latitude !== undefined && longitude !== undefined) {
            set.location = {
              coordinate_reference_system: metadata.coordinateReferenceSystem,
              coordinates_type: 'Geodetic',
              azimuth_reference: 'True',
              latitude,
              longitude
            };
          } else {
            errors.push({
              row: row.rowNumber,
              column: 'Latitude / Longitude',
              message: 'Latitude and longitude must be valid decimal-degree or DMS coordinates.',
              suggestion: 'Enter decimal degrees or DMS values with N/S and E/W directions.'
            });
          }
        }
      }

      const target: TargetPoint = {
        id: `${wellName}-${pointCode}`,
        name: pointFullName,
        pointCode,
        easting: toMetres(easting as number),
        northing: toMetres(northing as number)
      };
      if (typeof tvd === 'number' && !Number.isNaN(tvd)) target.tvdss = toMetres(tvd);

      set.targets.push(target);
    }

    const sets = Array.from(setsByWell.values()).filter((set) => set.targets.length > 0);

    if (sets.length === 0 && errors.length === 0) {
      throw new Error('No target points found in the file.');
    }

    const model: TargetsModel = { metadata, sets };
    if (provisionsParents) model.provisionParents = true;
    if (errors.length) model.errors = errors;
    return model;
  }

  /**
   * Extract the North Reference / Coordinate Type / Units / RKB metadata
   * rows that precede the well-data header (AC2). Missing/empty/non-numeric
   * values are reported as validation errors rather than thrown, so the well
   * data can still be parsed.
   */
  private extractTargetsMetadata(
    rows: WorksheetRow[],
    headerRowIndex: number,
    errors: TargetValidationError[]
  ): TargetsMetadata {
    const metadataRows = rows.slice(0, headerRowIndex);
    const trimmed = (v: string | undefined) => (v ?? '').toString().trim();
    const headerRowNumber = rows[headerRowIndex]?.rowNumber ?? 0;

    const findValue = (label: string): { row: WorksheetRow; value: string } | undefined => {
      const labelKey = inputLabelKey(label);
      const row = metadataRows.find((r) => r.cells.some((c) => inputLabelKey(c) === labelKey));
      if (!row) return undefined;
      const labelIndex = row.cells.findIndex((c) => inputLabelKey(c) === labelKey);
      const value = trimmed(row.cells.slice(labelIndex + 1).find((c) => trimmed(c) !== ''));
      return { row, value };
    };

    const metadata: TargetsMetadata = {};

    const readString = (label: string, key: 'northReference' | 'coordinateType' | 'units', example: string) => {
      const found = findValue(label);
      if (!found) {
        errors.push({
          row: headerRowNumber,
          column: label,
          message: `Required header field "${label}" was not found.`,
          suggestion: `Add a "${label}" row above the well data table (e.g. "${label},${example}").`
        });
        return;
      }
      if (!found.value) {
        errors.push({
          row: found.row.rowNumber,
          column: label,
          message: `${label} value is empty.`,
          suggestion: `Enter a value next to "${label}" (e.g. "${label},${example}").`
        });
        return;
      }
      metadata[key] = found.value;
    };

    readString('North Reference', 'northReference', 'Grid');
    readString('Coordinate Type', 'coordinateType', 'Grid');
    readString('Units', 'units', 'feet');

    const readOptionalString = (
      labels: string[],
      key: 'azimuthReference' | 'geodeticSystem' | 'geodeticDatum' | 'geodeticZone'
    ) => {
      const found = labels.map((label) => findValue(label)).find((value) => value !== undefined);
      if (found?.value) metadata[key] = found.value;
    };
    readOptionalString(['azimuth_reference', 'Azimuth Reference'], 'azimuthReference');
    readOptionalString(['Geodetic system'], 'geodeticSystem');
    readOptionalString(['Geodetic datum'], 'geodeticDatum');
    readOptionalString(['Geodetic Zone'], 'geodeticZone');

    if (metadata.geodeticSystem && metadata.geodeticDatum && metadata.geodeticZone) {
      metadata.coordinateReferenceSystem = this.resolveCrs(metadataRows).crs;
    }

    const rkbFound = findValue('RKB');
    if (!rkbFound) {
      errors.push({
        row: headerRowNumber,
        column: 'RKB',
        message: 'Required header field "RKB" was not found.',
        suggestion: 'Add an "RKB" row above the well data table (e.g. "RKB,28.5").'
      });
    } else if (!rkbFound.value) {
      errors.push({
        row: rkbFound.row.rowNumber,
        column: 'RKB',
        message: 'RKB value is empty.',
        suggestion: 'Enter a value next to "RKB" (e.g. "RKB,28.5").'
      });
    } else {
      const n = Number(rkbFound.value);
      if (!Number.isFinite(n)) {
        errors.push({
          row: rkbFound.row.rowNumber,
          column: 'RKB',
          message: 'Value is not numeric.',
          suggestion: 'Enter a numeric RKB value (e.g. 28.5).'
        });
      } else {
        metadata.rkb = n;
      }
    }

    return metadata;
  }

  // --- Unified model building (format-agnostic) ---

  private buildModelFromRows(rows: WorksheetRow[]): ModelData {
    const stationColumns = this.findStationColumns(rows);
    const units = { depthInFeet: stationColumns.depthInFeet, anglesInDegrees: stationColumns.anglesInDegrees };
    const metadata = this.extractMetadata(rows, units);
    const stations = this.extractStations(rows, stationColumns);

    if (stations.length === 0) {
      throw new Error('No trajectory stations found in the file.');
    }

    const wellId = metadata.wellName;
    const trajectoryId = metadata.wellboreId + ' - Trajectory';
    const trajectoryName = metadata.trajectoryName;

    const model: ModelData = {
      well: {
        id: wellId,
        name: metadata.wellName,
        fetchable: true,
        location: {
          coordinate_reference_system: metadata.coordinateReferenceSystem,
          coordinates_type: metadata.azimuthReference,
          azimuth_reference: metadata.azimuthReference,
          northing: metadata.northing,
          easting: metadata.easting,
          latitude: metadata.latitude,
          longitude: metadata.longitude
        },
        is_off_shore: false,
        ground_level: metadata.groundLevel,
        rkb: metadata.rkb + metadata.groundLevel,
        uwi: metadata.uwi,
        field_name: metadata.fieldName,
        timestamp: new Date().toISOString()
      },
      wellbore: {
        id: metadata.wellboreId,
        name: metadata.wellboreName,
        fetchable: true,
        well_id: wellId,
        ubhi: metadata.wellboreId + '-uwi',
        timestamp: new Date().toISOString()
      },
      trajectory: {
        id: trajectoryId,
        name: trajectoryName,
        fetchable: true,
        wellbore_id: metadata.wellboreId,
        is_actual: false,
        is_working_survey: false,
        rkb: metadata.rkb + metadata.groundLevel,
        azimuth_reference: metadata.azimuthReference,
        vsec: {
          origin_ns: metadata.vsecOriginNs,
          origin_ew: metadata.vsecOriginEw,
          azimuth: metadata.vsecAzimuth
        },
        stations,
        timestamp: new Date().toISOString(),
        survey_date: new Date().toISOString()
      }
    };

    if (metadata.crsWarning) {
      model.warnings = [metadata.crsWarning];
    }

    return model;
  }

  // --- Abstract metadata extraction (searches by label) ---

  private extractMetadata(rows: WorksheetRow[], units: { depthInFeet: boolean; anglesInDegrees: boolean }): ExtractedMetadata {
    const wellName = this.findLabeledValue(rows, [/^well$/i]) || '';
    const wellboreName = this.findLabeledValue(rows, [/^wellbore$/i]) || '';
    const fieldName = this.findLabeledValue(rows, [/^field$/i]) || '';
    const azimuthReference = this.findLabeledValue(rows, [/north\s*reference/i, /azimuth\s*reference/i]) || 'Grid';
    const uwi = this.findLabeledValue(rows, [/^uwi$/i, /^api\s*number$/i, /^unique\s*well\s*id(?:entifier)?$/i]) || '';

    // Trajectory name: WellArchitect uses "WellPath" label; Baker uses "Name" label.
    // Fall back to wellboreName + ' - Trajectory' if neither is found.
    const trajectoryName =
      this.findLabeledValue(rows, [/^wellpath$/i]) ||
      this.findLabeledValue(rows, [/^name$/i]) ||
      wellboreName + ' - Trajectory';

    if (!wellName) {
      throw new Error('Well name not found. Ensure the file contains a "Well" label with a value.');
    }
    if (!wellboreName) {
      throw new Error('Wellbore name not found. Ensure the file contains a "Wellbore" label with a value.');
    }

    const wellboreId = wellboreName.trim();
    const { crs, warning: crsWarning } = this.resolveCrs(rows);
    const { latitude, longitude } = this.resolveLatLong(rows);
    const { northing, easting } = this.resolveNorthingEasting(rows);
    const { groundLevel, rkb } = this.resolveElevations(rows, units);
    const { originNs, originEw, azimuth } = this.resolveVsec(rows, units);

    return {
      wellName: wellName.trim(),
      wellboreId,
      wellboreName: wellboreName.trim(),
      trajectoryName: trajectoryName.trim(),
      fieldName: fieldName.trim(),
      coordinateReferenceSystem: crs,
      crsWarning,
      azimuthReference: azimuthReference.trim(),
      latitude,
      longitude,
      northing,
      easting,
      groundLevel,
      rkb,
      vsecOriginNs: originNs,
      vsecOriginEw: originEw,
      vsecAzimuth: azimuth,
      uwi
    };
  }

  /**
   * Search rows for a label matching any of the given patterns and return
   * the adjacent value (next non-empty cell to the right, or on the same row).
   */
  private findLabeledValue(rows: WorksheetRow[], labelPatterns: RegExp[]): string | undefined {
    for (const row of rows) {
      for (let col = 0; col < row.cells.length; col++) {
        const cell = row.cells[col];
        if (!cell) continue;
        const normalizedLabel = normalizeInputLabel(cell);

        for (const pattern of labelPatterns) {
          if (pattern.test(normalizedLabel)) {
            // Return the next non-empty cell to the right
            for (let next = col + 1; next < row.cells.length; next++) {
              const val = row.cells[next]?.trim();
              if (val) return val;
            }
          }
        }
      }
    }
    return undefined;
  }

  /**
   * Search rows for any cell whose content matches a pattern and return it.
   */
  private findCellMatching(rows: WorksheetRow[], pattern: RegExp): string | undefined {
    for (const row of rows) {
      for (const cell of row.cells) {
        if (cell && pattern.test(cell.trim())) {
          return cell.trim();
        }
      }
    }
    return undefined;
  }

  private resolveCrs(rows: WorksheetRow[]): { crs: string; warning?: string } {
    // Try Compass format: separate Geodetic system/datum/zone labels
    const geodeticSystem = this.findLabeledValue(rows, [/geodetic\s*system/i]);
    const geodeticDatum = this.findLabeledValue(rows, [/geodetic\s*datum/i]);
    const mapZone = this.findLabeledValue(rows, [/geodetic\s*zone/i, /map\s*zone/i]);

    if (geodeticSystem && geodeticDatum && mapZone) {
      const readable = `${geodeticSystem.trim()} / ${geodeticDatum.trim()} / ${mapZone.trim()}`;
      const crsKey = `${geodeticSystem.trim()}|${geodeticDatum.trim()}|${mapZone.trim()}`;
      const mapped = this.crsMapping?.get(crsKey);
      if (mapped) {
        // Guard 1 (hard block): an ambiguous source key resolves to more than one
        // real-world CRS (e.g. UTM + WGS84 zones that differ by country/region).
        // Auto-selecting or passing through would be a silent, possibly-wrong
        // guess, so this remains an error that blocks the import.
        if (this.ambiguousCrsKeys.has(crsKey)) {
          throw new Error(
            `Ambiguous coordinate reference system for "${readable}": this source key matches multiple ` +
            `coordinate reference systems (resolution depends on country/region). Resolve the CRS manually before importing.`
          );
        }
        return { crs: mapped };
      }
      // Guard 2 (soft fallback): no registry mapping exists — use the file's CRS
      // as-is (no conversion) and surface a warning rather than blocking.
      return {
        crs: readable,
        warning:
          `Coordinate reference system "${readable}" is not in CRS_Mapping.csv. ` +
          `Using the value from the file without conversion — verify it is valid in DrillPlan before relying on it.`
      };
    }

    // Try WellArchitect format: "Projection System" label
    const projSystem = this.findLabeledValue(rows, [/projection\s*system/i]);
    if (projSystem) {
      // Try to resolve to a platform-registered CRS name via the WA mapping
      const mapped = this.waMapping?.get(projSystem.trim().toLowerCase());
      if (mapped) return { crs: mapped };
      // Soft fallback: pass the raw projection system through with a warning.
      return {
        crs: projSystem.trim(),
        warning:
          `Coordinate reference system "${projSystem.trim()}" (WellArchitect projection system) is not in CRS_Mapping.csv. ` +
          `Using the value from the file without conversion — verify it is valid in DrillPlan before relying on it.`
      };
    }

    // Partial Compass data: datum + zone but no geodetic system — pass through
    // the combined raw value with a warning.
    if (geodeticDatum && mapZone) {
      const readable = `${geodeticDatum.trim()} / ${mapZone.trim()}`;
      return {
        crs: readable,
        warning:
          `Incomplete coordinate reference system source data (missing geodetic system). ` +
          `Using "${readable}" from the file without conversion — verify it is valid in DrillPlan before relying on it.`
      };
    }

    // No coordinate reference system information found at all — warn but allow
    // the import to proceed without a CRS.
    return {
      crs: '',
      warning:
        `No coordinate reference system found in the source file. ` +
        `The well will be imported without a CRS — set one manually if DrillPlan requires it.`
    };
  }

  private resolveLatLong(rows: WorksheetRow[]): { latitude: number; longitude: number } {
    let latitude = 0;
    let longitude = 0;

    // Search for DMS: e.g. "32° 9' 14.243 N 103° 39' 58.681 W"
    const dmsPattern = /([\d.]+)[°]\s*([\d.]+)['']\s*([\d.]+)\s*([NS])\s*([\d.]+)[°]\s*([\d.]+)['']\s*([\d.]+)\s*([EW])/i;

    // Try "LAT/LONG" label first (Compass format)
    const latLongValue = this.findLabeledValue(rows, [/lat\s*\/\s*long/i, /lat.*long/i]);
    if (latLongValue) {
      const match = dmsPattern.exec(latLongValue);
      if (match) {
        latitude = this.dmsToDecimal(Number(match[1]), Number(match[2]), Number(match[3]), match[4]);
        longitude = this.dmsToDecimal(Number(match[5]), Number(match[6]), Number(match[7]), match[8]);
        return { latitude, longitude };
      }
    }

    // Try "Slot Location" row (WellArchitect format) — look for Latitude/Longitude columns
    for (const row of rows) {
      const firstCell = row.cells[0]?.trim();
      if (firstCell && /slot\s*location/i.test(firstCell)) {
        for (const cell of row.cells) {
          if (!cell) continue;
          // decimal degrees with ° symbol: "40.3712700°"
          const decMatch = cell.match(/([\d.]+)°/);
          if (decMatch) {
            const val = Number(decMatch[1]);
            // Heuristic: latitude < 90, longitude > 90 (for US)
            if (val > 90 && longitude === 0) longitude = -val; // West hemisphere assumed
            else if (latitude === 0) latitude = val;
          }
        }
        break;
      }
    }

    // Fallback: search any cell for DMS pattern
    if (latitude === 0 && longitude === 0) {
      for (const row of rows) {
        for (const cell of row.cells) {
          if (!cell) continue;
          const match = dmsPattern.exec(cell);
          if (match) {
            latitude = this.dmsToDecimal(Number(match[1]), Number(match[2]), Number(match[3]), match[4]);
            longitude = this.dmsToDecimal(Number(match[5]), Number(match[6]), Number(match[7]), match[8]);
            return { latitude, longitude };
          }
        }
      }
    }

    // WellArchitect: look for Latitude column in Slot Location row
    if (latitude === 0) {
      const slotRow = this.findRowByLabel(rows, /slot\s*location/i);
      if (slotRow) {
        const headerRow = rows.find(r => r.rowNumber === slotRow.rowNumber - 2 || r.rowNumber === slotRow.rowNumber - 1);
        if (headerRow) {
          const latCol = headerRow.cells.findIndex(c => c && /latitude/i.test(c));
          const lonCol = headerRow.cells.findIndex(c => c && /longitude/i.test(c));
          if (latCol >= 0 && slotRow.cells[latCol]) {
            const latStr = slotRow.cells[latCol]!.replace('°', '');
            latitude = Number(latStr) || 0;
          }
          if (lonCol >= 0 && slotRow.cells[lonCol]) {
            const lonStr = slotRow.cells[lonCol]!.replace('°', '').replace('-', '');
            longitude = -(Number(lonStr) || 0);
          }
        }
      }
    }

    return { latitude, longitude };
  }

  private resolveNorthingEasting(rows: WorksheetRow[]): { northing: number; easting: number } {
    let northing = 0;
    let easting = 0;

    // Compass format: "Northings/Eastings (usft):" label with "420370.14 N 706432.53 E"
    const neValue = this.findLabeledValue(rows, [/northing.*easting/i, /easting.*northing/i]);
    if (neValue) {
      const match = neValue.match(/([\d.]+)\s*N\s+([\d.]+)\s*E/i);
      if (match) {
        northing = Number(match[1]);
        easting = Number(match[2]);
        return { northing, easting };
      }
    }

    // WellArchitect format: "WELLPATH LOCATION" row contains the section label AND the
    // column headers (Easting, Northing, ...) on the SAME row — no separate header row.
    const locationHeaderRow = rows.find(r =>
      r.cells.some(c => c && /^wellpath\s*location$/i.test(c.trim()))
    );
    if (locationHeaderRow) {
      const headerIdx = locationHeaderRow.rowNumber;
      // Column headers may be on the WELLPATH LOCATION row itself, or a later row
      const colHeaderRow = locationHeaderRow.cells.some(c => c && /easting/i.test(c))
        ? locationHeaderRow
        : rows.find(r => r.rowNumber > headerIdx && r.cells.some(c => c && /easting/i.test(c)));
      const slotRow = rows.find(r => r.rowNumber > headerIdx && r.cells.some(c => c && /slot\s*location/i.test(c.trim())));
      if (colHeaderRow && slotRow) {
        const eastCol = colHeaderRow.cells.findIndex(c => c && /easting/i.test(c));
        const northCol = colHeaderRow.cells.findIndex(c => c && /northing/i.test(c));
        if (eastCol >= 0 && slotRow.cells[eastCol]) easting = Number(slotRow.cells[eastCol]) || 0;
        if (northCol >= 0 && slotRow.cells[northCol]) northing = Number(slotRow.cells[northCol]) || 0;
      }
    }

    return { northing, easting };
  }

  private resolveElevations(rows: WorksheetRow[], units: { depthInFeet: boolean }): { groundLevel: number; rkb: number } {
    let groundLevel = 0;
    let rkb = 0;
    const toMeters = (v: number) => units.depthInFeet ? v * 0.3048 : v;

    // Pattern: "GL <number>" and "RKB <number>" (feet) - Compass format in a combined cell
    // OR: "TVD Reference Datum" label with "(GL 5000ft + RKB 28.5ft)" style
    // OR: WellArchitect "Vertical Reference Point" with "(4900'GL+30'KB = 4930.0'RKB)"

    // Search for GL+RKB pattern in any cell
    const glRkbPattern = /(\d+(?:\.\d+)?)['']*\s*GL\s*\+\s*(\d+(?:\.\d+)?)['']*\s*KB/i;
    const glRkbAlt = /GL\s+(\d+(?:\.\d+)?)\s*(?:ft|')?.*?RKB\s+(\d+(?:\.\d+)?)/i;
    const glRkbAlt2 = /GL\s+(\d+(?:\.\d+)?)\s*(?:ft|')?.*?KB\s+(\d+(?:\.\d+)?)/i;

    for (const row of rows) {
      for (const cell of row.cells) {
        if (!cell) continue;
        const s = cell.trim();

        // WellArchitect: "(4900'GL+30'KB = 4930.0'RKB)"
        let match = glRkbPattern.exec(s);
        if (match) {
          groundLevel = toMeters(Number(match[1]));
          rkb = toMeters(Number(match[2]));
          return { groundLevel, rkb };
        }

        // Compass: "GL 5000ft + RKB 28.5ft" or label-based
        match = glRkbAlt.exec(s);
        if (match) {
          groundLevel = toMeters(Number(match[1]));
          rkb = toMeters(Number(match[2]));
          return { groundLevel, rkb };
        }

        match = glRkbAlt2.exec(s);
        if (match) {
          groundLevel = toMeters(Number(match[1]));
          rkb = toMeters(Number(match[2]));
          return { groundLevel, rkb };
        }
      }
    }

    // Try labeled lookups
    const tvdRef = this.findLabeledValue(rows, [/tvd\s*reference\s*datum/i, /vertical\s*reference\s*point/i]);
    if (tvdRef) {
      const glMatch = tvdRef.match(/GL\s+([\d.]+)/i);
      const rkbMatch = tvdRef.match(/(?:RKB|KB)\s+([\d.]+)/i);
      if (glMatch) groundLevel = toMeters(Number(glMatch[1]));
      if (rkbMatch) rkb = toMeters(Number(rkbMatch[1]));
    }

    // Compass format separate labels
    const glElevation = this.findLabeledValue(rows, [/ground\s*level\s*elevation/i]);
    if (glElevation) {
      const m = glElevation.match(/([\d.]+)/);
      if (m) groundLevel = toMeters(Number(m[1]));
    }

    const kbHeight = this.findLabeledValue(rows, [/kb\s*height/i]);
    if (kbHeight) {
      const m = kbHeight.match(/([\d.]+)/);
      if (m) rkb = toMeters(Number(m[1]));
    }

    return { groundLevel, rkb };
  }

  private resolveVsec(rows: WorksheetRow[], units: { anglesInDegrees: boolean }): { originNs: number; originEw: number; azimuth: number } {
    let originNs = 0;
    let originEw = 0;
    let azimuth = 0;
    const toRadians = (v: number) => units.anglesInDegrees ? v * Math.PI / 180 : v;

    // Compass format: "Vertical Section Azimuth:" and "Vertical Section Origin:"
    const vsecAzLabel = this.findLabeledValue(rows, [
      /vertical\s*section\s*azimuth/i,
      /section\s*azimuth/i
    ]);
    if (vsecAzLabel) {
      const match = vsecAzLabel.match(/([\d.]+)/);
      if (match) azimuth = toRadians(Number(match[1]));
    }

    const vsecOriginLabel = this.findLabeledValue(rows, [
      /vertical\s*section\s*origin/i,
      /section\s*origin/i
    ]);
    if (vsecOriginLabel) {
      const match = vsecOriginLabel.match(/([\d.]+)\s*N\s+.*?([\d.]+)\s*E/i);
      if (match) {
        originNs = Number(match[1]);
        originEw = Number(match[2]);
      }
    }

    return { originNs, originEw, azimuth };
  }

  // --- Station detection and extraction ---

  /**
   * Dynamically find the station data header row by looking for
   * columns named MD, Inc/Inclination, Azi/Azimuth.
   */
  private findStationColumns(rows: WorksheetRow[]): StationColumns {
    const mdPatterns = [/^md$/i, /measured\s*depth/i];
    const incPatterns = [/^inc$/i, /^inclination$/i, /^incl$/i];
    const aziPatterns = [/^azi$/i, /^azimuth$/i];

    for (let i = 0; i < rows.length; i++) {
      const row = rows[i];
      let mdCol = -1;
      let incCol = -1;
      let aziCol = -1;
      let tvdCol = -1;
      let northCol = -1;
      let eastCol = -1;
      let commentCol = -1;

      for (let col = 0; col < row.cells.length; col++) {
        const cell = row.cells[col]?.trim();
        if (!cell) continue;

        if (mdCol < 0 && mdPatterns.some(p => p.test(cell))) mdCol = col;
        if (incCol < 0 && incPatterns.some(p => p.test(cell))) incCol = col;
        if (aziCol < 0 && aziPatterns.some(p => p.test(cell))) aziCol = col;
        if (tvdCol < 0 && /^tvd$/i.test(cell)) tvdCol = col;
        if (northCol < 0 && /^north/i.test(cell) && !/north\s*ref/i.test(cell)) northCol = col;
        if (eastCol < 0 && /^east/i.test(cell)) eastCol = col;
        if (commentCol < 0 && /comment/i.test(cell)) commentCol = col;
      }

      if (mdCol >= 0 && incCol >= 0 && aziCol >= 0) {
        // Detect units from the row immediately following the header
        let depthInFeet = true;       // conservative default: assume feet
        let anglesInDegrees = true;   // conservative default: assume degrees
        let hasUnitsRow = false;

        if (i + 1 < rows.length) {
          const nextRow = rows[i + 1];
          const mdCell = (nextRow.cells[mdCol] ?? '').trim();
          const incCell = (nextRow.cells[incCol] ?? '').trim();
          const aziCell = (nextRow.cells[aziCol] ?? '').trim();

          hasUnitsRow = [mdCell, incCell, aziCell].some(
            c => /\[|\bft\b|\bm\b|\bdeg\b|°|\brad\b|\busft\b/i.test(c)
          );

          if (hasUnitsRow) {
            const normMd  = mdCell.replace(/[\[\]()]/g, '').trim().toLowerCase();
            const normInc = incCell.replace(/[\[\]()]/g, '').trim().toLowerCase();
            // Only skip ft→m conversion if the unit is explicitly meters
            depthInFeet   = normMd  !== 'm' && normMd  !== 'meters' && normMd  !== 'metre';
            // Only skip deg→rad conversion if the unit is explicitly radians
            anglesInDegrees = normInc !== 'rad' && normInc !== 'radians';
          }
        }

        return {
          md: mdCol,
          inclination: incCol,
          azimuth: aziCol,
          tvd: tvdCol,
          northing: northCol,
          easting: eastCol,
          comment: commentCol,
          headerRowIndex: i,
          depthInFeet,
          anglesInDegrees,
          hasUnitsRow
        };
      }
    }

    throw new Error('Could not find station data header (MD/Inclination/Azimuth columns) in the file.');
  }

  private extractStations(rows: WorksheetRow[], columns: StationColumns): ModelData['trajectory']['stations'] {
    const stations: ModelData['trajectory']['stations'] = [];
    const toMeters  = (v: number) => columns.depthInFeet   ? v * 0.3048       : v;
    const toRadians = (v: number) => columns.anglesInDegrees ? v * Math.PI / 180 : v;

    const dataStartIdx = columns.headerRowIndex + 1;
    const startIdx = columns.hasUnitsRow ? dataStartIdx + 1 : dataStartIdx;

    for (let i = startIdx; i < rows.length; i++) {
      const row = rows[i];

      // Stop when a post-wellpath section header is detected (e.g. TARGETS, CASINGS, DESIGN COMMENTS)
      if (stations.length > 0 && this.isStationSectionEnd(row)) break;

      const mdRaw = row.cells[columns.md];
      const incRaw = row.cells[columns.inclination];
      const aziRaw = row.cells[columns.azimuth];

      if (!mdRaw || String(mdRaw).trim() === '') continue;

      // Skip non-numeric MD values (section headers, labels embedded in data area)
      const mdNum = Number(mdRaw);
      if (!Number.isFinite(mdNum)) continue;

      const incNum = Number(incRaw);
      const aziNum = Number(aziRaw);

      // Skip rows where inc/azimuth are non-numeric — they belong to a different
      // section (e.g. SURVEY PROGRAM, TARGETS) that reuses the same column positions
      if (!Number.isFinite(incNum) || !Number.isFinite(aziNum)) continue;

      // Convert feet → meters, degrees → radians
      const station: ModelData['trajectory']['stations'][number] = {
        md: toMeters(mdNum),
        inclination: toRadians(incNum),
        azimuth: toRadians(aziNum),
        comment: ''
      };

      if (columns.tvd >= 0 && row.cells[columns.tvd]) {
        const tvd = Number(row.cells[columns.tvd]);
        if (Number.isFinite(tvd)) station.tvd = toMeters(tvd);
      }

      if (columns.northing >= 0 && row.cells[columns.northing]) {
        const n = Number(row.cells[columns.northing]);
        if (Number.isFinite(n)) station.northing = toMeters(n);
      }

      if (columns.easting >= 0 && row.cells[columns.easting]) {
        const e = Number(row.cells[columns.easting]);
        if (Number.isFinite(e)) station.easting = toMeters(e);
      }

      if (columns.comment >= 0 && row.cells[columns.comment]) {
        station.comment = row.cells[columns.comment]!.trim();
      }

      stations.push(station);
    }

    return stations;
  }

  // --- CSV parsing ---

  private parseCsvToRows(csvText: string): WorksheetRow[] {
    const lines = csvText.split(/\r?\n/);
    const rows: WorksheetRow[] = [];

    for (let i = 0; i < lines.length; i++) {
      const cells = this.parseCsvLine(lines[i]);
      rows.push({ rowNumber: i + 1, cells });
    }

    return rows;
  }

  private parseCsvLine(line: string): (string | undefined)[] {
    const cells: (string | undefined)[] = [];
    let current = '';
    let inQuotes = false;

    for (let i = 0; i < line.length; i++) {
      const char = line[i];
      if (char === '"') {
        if (inQuotes && line[i + 1] === '"') {
          current += '"';
          i++;
        } else {
          inQuotes = !inQuotes;
        }
      } else if (char === ',' && !inQuotes) {
        cells.push(current.trim() || undefined);
        current = '';
      } else {
        current += char;
      }
    }
    cells.push(current.trim() || undefined);

    return cells;
  }

  // --- CRS mapping ---

  private async loadCrsMapping(): Promise<void> {
    const csvText = await firstValueFrom(
      this.http.get('assets/CRS_Mapping.csv', { responseType: 'text' })
    );
    const { compassMap, waMap, ambiguousKeys } = this.parseCrsMappingCsv(csvText);
    this.crsMapping = compassMap;
    this.waMapping = waMap;
    this.ambiguousCrsKeys = ambiguousKeys;
  }

  private parseCrsMappingCsv(csvText: string): { compassMap: Map<string, string>; waMap: Map<string, string>; ambiguousKeys: Set<string> } {
    const compassMap = new Map<string, string>();
    const waMap = new Map<string, string>();
    const ambiguousKeys = new Set<string>();
    const lines = csvText.split('\n');

    for (let i = 1; i < lines.length; i++) {
      const line = lines[i].trim();
      if (!line) continue;

      const parts = this.parseCsvLine(line).map(p => p?.trim() || '');

      if (parts.length >= 4 && parts[3]) {
        // Compass 3-key lookup
        const key = `${parts[0]}|${parts[1]}|${parts[2]}`;

        // Guard 1: a key that already maps to a *different* CRS value is ambiguous
        // (the same source system/datum/zone resolves to more than one CRS).
        const existing = compassMap.get(key);
        if (existing !== undefined && existing !== parts[3]) {
          ambiguousKeys.add(key);
        }
        compassMap.set(key, parts[3]);

        // Optional 5th column: WellArchitect "Projection System" string
        if (parts[4]) {
          waMap.set(parts[4].toLowerCase(), parts[3]);
        }
      }
    }

    return { compassMap, waMap, ambiguousKeys };
  }

  // --- Helpers ---

  private dmsToDecimal(degrees: number, minutes: number, seconds: number, direction: string): number {
    let decimal = degrees + minutes / 60 + seconds / 3600;
    if (/[SW]/i.test(direction)) decimal = -decimal;
    return decimal;
  }

  private parseGeographicCoordinate(value: string, axis: 'latitude' | 'longitude'): number | undefined {
    const text = value.trim();
    const dms = text.match(
      /^([0-9]+(?:\.[0-9]+)?)\s*(?:°|DEG)\s*([0-9]+(?:\.[0-9]+)?)\s*(?:'|′)\s*([0-9]+(?:\.[0-9]+)?)\s*(?:"|″)?\s*([NSEW])$/i
    );
    let coordinate: number;
    let direction = '';
    if (dms) {
      const minutes = Number(dms[2]);
      const seconds = Number(dms[3]);
      if (minutes >= 60 || seconds >= 60) return undefined;
      direction = dms[4].toUpperCase();
      coordinate = this.dmsToDecimal(Number(dms[1]), minutes, seconds, direction);
    } else {
      const decimal = text.match(/^([+-]?[0-9]+(?:\.[0-9]+)?)\s*°?\s*([NSEW])?$/i);
      if (!decimal) return undefined;
      coordinate = Number(decimal[1]);
      direction = (decimal[2] ?? '').toUpperCase();
      if (direction) {
        coordinate = Math.abs(coordinate) * (/[SW]/.test(direction) ? -1 : 1);
      }
    }

    if (axis === 'latitude') {
      if (direction && !/[NS]/.test(direction)) return undefined;
      return coordinate >= -90 && coordinate <= 90 ? coordinate : undefined;
    }
    if (direction && !/[EW]/.test(direction)) return undefined;
    return coordinate >= -180 && coordinate <= 180 ? coordinate : undefined;
  }

  /**
   * Returns true when a row looks like a WellArchitect section header that
   * signals the end of wellpath station data (TARGETS, CASINGS, SURVEY PROGRAM,
   * DESIGN COMMENTS, COMMENTS, etc.).
   */
  private isStationSectionEnd(row: WorksheetRow): boolean {
    const firstNonEmpty = row.cells.find(c => c && c.trim())?.trim();
    if (!firstNonEmpty || firstNonEmpty.length < 4) return false;

    // Spaced-letter titles like "T A R G E T S", "C O M M E N T S"
    if (/^[A-Z](\s[A-Z]){3,}/i.test(firstNonEmpty)) return true;

    // Known WellArchitect section keywords (may contain well name / digits after keyword)
    if (/^(HOLE AND CASING|SURVEY PROGRAM|DESIGN COMMENT|TARGET|COMMENT|CASING)/i.test(firstNonEmpty)) return true;

    return false;
  }

  private findRowByLabel(rows: WorksheetRow[], pattern: RegExp): WorksheetRow | undefined {
    return rows.find(r => r.cells.some(c => c && pattern.test(c.trim())));
  }

  // --- ZIP / XLSX parsing utilities ---

  private findEndOfCentralDirectory(view: DataView): number {
    const minimumSize = 22;
    for (let offset = view.byteLength - minimumSize; offset >= 0; offset -= 1) {
      if (view.getUint32(offset, true) === EOCD_SIGNATURE) {
        return offset;
      }
    }
    throw new Error('Could not locate end of central directory in XLSX file.');
  }

  private readCentralDirectory(buffer: ArrayBuffer, view: DataView): Map<string, ZipEntry> {
    const eocdOffset = this.findEndOfCentralDirectory(view);
    const centralDirectoryOffset = view.getUint32(eocdOffset + 16, true);
    const totalEntries = view.getUint16(eocdOffset + 10, true);

    const entries = new Map<string, ZipEntry>();
    let offset = centralDirectoryOffset;

    for (let index = 0; index < totalEntries; index++) {
      const signature = view.getUint32(offset, true);
      if (signature !== CENTRAL_DIRECTORY_SIGNATURE) {
        throw new Error('Invalid central directory signature in XLSX file.');
      }

      const compressionMethod = view.getUint16(offset + 10, true);
      const compressedSize = view.getUint32(offset + 20, true);
      const localHeaderOffset = view.getUint32(offset + 42, true);
      const fileNameLength = view.getUint16(offset + 28, true);
      const extraFieldLength = view.getUint16(offset + 30, true);
      const commentLength = view.getUint16(offset + 32, true);

      const nameStart = offset + 46;
      const fileNameBytes = new Uint8Array(buffer, nameStart, fileNameLength);
      const fileName = new TextDecoder('utf-8').decode(fileNameBytes);

      entries.set(fileName, new ZipEntry(buffer, {
        compressionMethod,
        compressedSize,
        localHeaderOffset
      }));

      offset = nameStart + fileNameLength + extraFieldLength + commentLength;
    }

    return entries;
  }

  private parseSharedStrings(xmlText: string): string[] {
    if (!xmlText) return [];
    const doc = new DOMParser().parseFromString(xmlText, 'application/xml');
    const nodes = Array.from(doc.getElementsByTagName('si'));
    return nodes.map((si) => {
      const textNodes = si.getElementsByTagName('t');
      let combined = '';
      for (let i = 0; i < textNodes.length; i++) {
        combined += textNodes[i].textContent || '';
      }
      return combined;
    });
  }

  private parseXlsxWorksheet(xmlText: string, sharedStrings: string[]): WorksheetRow[] {
    const doc = new DOMParser().parseFromString(xmlText, 'application/xml');
    const rows: WorksheetRow[] = [];
    const rowNodes = doc.getElementsByTagName('row');

    for (let i = 0; i < rowNodes.length; i++) {
      const rowNode = rowNodes[i];
      const rowNumber = parseInt(rowNode.getAttribute('r') || `${i + 1}`, 10);
      const cells: (string | undefined)[] = [];
      const cellNodes = rowNode.getElementsByTagName('c');

      for (let j = 0; j < cellNodes.length; j++) {
        const cellNode = cellNodes[j];
        const reference = cellNode.getAttribute('r') || 'A1';
        const columnIndex = this.columnLetterToIndex(reference);
        const value = this.extractCellValue(cellNode, sharedStrings);
        cells[columnIndex] = value;
      }

      rows.push({ rowNumber, cells });
    }

    return rows;
  }

  private extractCellValue(cell: Element, sharedStrings: string[]): string {
    const type = cell.getAttribute('t');
    const valueNode = cell.getElementsByTagName('v')[0];

    if (type === 's') {
      if (!valueNode) return '';
      const index = parseInt(valueNode.textContent || '0', 10);
      return sharedStrings[index] ?? '';
    }

    if (type === 'b') {
      if (!valueNode) return '';
      return valueNode.textContent === '1' ? 'TRUE' : 'FALSE';
    }

    if (type === 'inlineStr') {
      const inlineNode = cell.getElementsByTagName('is')[0];
      if (!inlineNode) return '';
      const textNode = inlineNode.getElementsByTagName('t')[0];
      return textNode?.textContent || '';
    }

    if (valueNode) return valueNode.textContent || '';

    const textNode = cell.getElementsByTagName('t')[0];
    return textNode?.textContent || '';
  }

  private columnLetterToIndex(reference: string): number {
    const match = /^([A-Z]+)(\d+)$/i.exec(reference);
    if (!match) return 0;
    const letters = match[1].toUpperCase();
    let index = 0;
    for (let i = 0; i < letters.length; i++) {
      index = index * 26 + (letters.charCodeAt(i) - 64);
    }
    return index - 1;
  }
}
